'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ForumPost {
  id: number;
  author: string;
  avatar: string;
  avatarAlt: string;
  day: number;
  title: string;
  content: string;
  likes: number;
  replies: number;
  timestamp: string;
}

const CommunityForum = () => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const forumPosts: ForumPost[] = [
  {
    id: 1,
    author: 'Sarah Martinez',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b23ad375-1763294352665.png",
    avatarAlt: 'Professional headshot of Hispanic woman with long dark hair smiling warmly',
    day: 7,
    title: 'Completed my first week!',
    content: 'I cannot believe how much has shifted in just 7 days. The forgiveness letter exercise on Day 6 brought up so much emotion, but I feel lighter now. Anyone else experience this?',
    likes: 24,
    replies: 8,
    timestamp: '2 hours ago'
  },
  {
    id: 2,
    author: 'James Chen',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1418365a1-1763293544915.png",
    avatarAlt: 'Asian man with glasses and friendly smile in casual business attire',
    day: 15,
    title: 'Struggling with self-forgiveness',
    content: 'Day 9 hit me hard. I find it easier to forgive others than myself. The meditation helped, but I am still working through guilt from past mistakes. Any advice from those further along?',
    likes: 31,
    replies: 12,
    timestamp: '5 hours ago'
  },
  {
    id: 3,
    author: 'Amara Okafor',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c10fb07b-1763299083921.png",
    avatarAlt: 'African woman with natural hair and warm smile wearing colorful traditional clothing',
    day: 22,
    title: 'Organizing our community celebration',
    content: 'Our local chapter is planning an amazing April 17th event! We have secured a park, lined up speakers, and are coordinating with 3 other faith communities. The toolkit has been invaluable.',
    likes: 45,
    replies: 15,
    timestamp: '1 day ago'
  },
  {
    id: 4,
    author: 'Michael Thompson',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18d5bd88d-1763296665838.png",
    avatarAlt: 'Caucasian man with short brown hair and confident expression in navy blue shirt',
    day: 30,
    title: 'Final day reflection - I am transformed',
    content: 'Tomorrow is April 17th and I am not the same person who started this journey 30 days ago. I have released resentments I carried for years. I am ready to live with no enemy.',
    likes: 67,
    replies: 23,
    timestamp: '3 hours ago'
  }];


  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
              Community Forum
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Loading community discussions...
            </p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-4">
            Community Forum
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Share your daily experiences, ask questions, and connect with fellow participants on the transformation journey.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Forum Posts */}
          <div className="flex-1 space-y-6">
            {forumPosts.map((post) =>
            <div
              key={post.id}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300">

                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <AppImage
                    src={post.avatar}
                    alt={post.avatarAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-base font-headline font-bold text-primary">
                        {post.author}
                      </h4>
                      <span className="px-2 py-0.5 bg-muted rounded-full text-xs font-body text-muted-foreground">
                        Day {post.day}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-body">
                      {post.timestamp}
                    </p>
                  </div>
                </div>
                <h3 className="text-lg font-headline font-bold text-primary mb-2">
                  {post.title}
                </h3>
                <p className="text-base text-foreground font-body leading-relaxed mb-4">
                  {post.content}
                </p>
                <div className="flex items-center gap-6">
                  <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                    <Icon name="HeartIcon" size={18} />
                    <span className="font-body">{post.likes}</span>
                  </button>
                  <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                    <Icon name="ChatBubbleLeftIcon" size={18} />
                    <span className="font-body">{post.replies} replies</span>
                  </button>
                  <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                    <Icon name="ShareIcon" size={18} />
                    <span className="font-body">Share</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* Create Post */}
            <div className="bg-card rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-headline font-bold text-primary mb-4">
                Share Your Experience
              </h3>
              <button className="w-full px-6 py-3 bg-accent text-accent-foreground rounded-lg font-cta text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-md">
                Create New Post
              </button>
            </div>

            {/* Popular Topics */}
            <div className="bg-card rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-headline font-bold text-primary mb-4">
                Popular Topics
              </h3>
              <div className="space-y-3">
                {['Forgiveness Challenges', 'Meditation Tips', 'Community Events', 'Success Stories', 'Daily Reflections'].map((topic, index) =>
                <button
                  key={index}
                  className="w-full text-left px-4 py-2 bg-muted rounded-lg text-sm font-body text-foreground hover:bg-background transition-colors duration-200">

                    {topic}
                  </button>
                )}
              </div>
            </div>

            {/* Community Stats */}
            <div className="bg-card rounded-xl p-6 shadow-md">
              <h3 className="text-lg font-headline font-bold text-primary mb-4">
                Community Stats
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground font-body">Active Members</span>
                  <span className="text-lg text-primary font-headline font-bold">2,847</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground font-body">Posts Today</span>
                  <span className="text-lg text-primary font-headline font-bold">34</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground font-body">Countries</span>
                  <span className="text-lg text-primary font-headline font-bold">28</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CommunityForum;